import React from 'react'
import Head from 'next/head'
import Link from 'next/link'

import BasePage from 'modules/marketplace/layout/components/BasePage'
import ButtonAnchor from 'components/ButtonAnchor'
import { RegularContainer } from 'modules/marketplace/layout/components/ResponsiveContainers'

import styles from '../../styles/integrations.module.css'

const MYOB_BRAND_COLOR = 'hsl(330, 97%, 47%)'
const MYOB_BRAND_COLOR_DARK = 'hsl(274, 69%, 34%)'

// TODO: add getStaticProps for static rendering
const MyobPage = () => (
  <BasePage hasMarketingFooter>
    <Head>
      <title>Integrate your sales and ordering seamlessly with MYOB AccountRight Live · Fieldfolio</title>
      <meta name="description" content="Integrate your sales and ordering seamlessly with MYOB AccountRight Live." />
      <link rel="canonical" href={`${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/integrations/myob`} />
    </Head>

    <RegularContainer>
      <div className={styles.logoHeader}>
        <img src="/marketing/integrations/myob/myob-developer-partner-logo.jpg" alt="MYOB Developer Partner" />
      </div>

      <div className={styles.pageHeader}>
        <h1 className="headline">Fieldfolio + MYOB: Seamless wholesale ordering</h1>
        <h2 className="subhead">Sync to MYOB AccountRight Live</h2>
        <p>
          The MYOB AccountRight Live integration enables new orders posted to your Fieldfolio seller account to be
          automatically synced to your selected MYOB AccountRight Live company file.
        </p>
        <div className={styles.ctaGroup}>
          <Link href="/signup/wholesaler" passHref>
            <ButtonAnchor kind="primary">Sell on Fieldfolio</ButtonAnchor>
          </Link>
          <ButtonAnchor
            className={styles.brandCtaButton}
            target="_blank"
            href="http://www.myob.com.au/trial/au/accountright-standard">
            Try MYOB for Free
          </ButtonAnchor>
        </div>
      </div>

      <div className={styles.pageSection}>
        <div className={styles.screenshotsContainer}>
          <img
            src="/marketing/integrations/myob/ff-screenshots-all.jpg"
            alt="Fieldfolio wholesale sales and ordering client apps"
          />
          <img src="/marketing/integrations/myob/ar-invoice.png" alt="MYOB AccountRight Invoice" />
        </div>
        <h3>Save time and eliminate data entry errors</h3>
        <h4>Order syncing</h4>
        <p>
          Fieldfolio automatically syncs orders from our iPad app and our online marketplace to your MYOB AccountRight
          Live company file, saving time and eliminating back-office errors.
        </p>
        <h4>Stock-on-hand syncing</h4>
        <p>
          Everytime an order is synced, the stock-on-hand level is retrieved from MYOB and updated in Fieldfolio to
          ensure you never sell more than you have in-stock.
        </p>
        <div className={styles.ctaGroup}>
          <Link href="/signup/wholesaler" passHref>
            <ButtonAnchor kind="primary">Sell on Fieldfolio</ButtonAnchor>
          </Link>
        </div>
      </div>
    </RegularContainer>

    <div
      className={styles.aboutSection}
      style={{
        color: 'white',
        background: `linear-gradient(hsl(250,80%,60%), hsl(260,70%,45%))`
      }}>
      <RegularContainer>
        <h2>Fieldfolio</h2>
        <h3>Australia&apos;s No.1 wholesale eCommerce platform.</h3>
        <h4>Online Marketplace with thousand of verified retail buyers</h4>
        <p>You bring the product catalog, we bring the buyers.</p>
        <h4>iPad sales and ordering app for Reps</h4>
        <p>Intuitive catalog display and ordering for iPad. Tradeshow friendly and full offline support.</p>

        <div className={styles.ctaGroup}>
          <Link href="/signup/wholesaler" passHref>
            <ButtonAnchor kind="reverse">Sell on Fieldfolio</ButtonAnchor>
          </Link>
        </div>
      </RegularContainer>
    </div>

    <div
      className={styles.aboutSection}
      style={{
        color: 'white',
        background: `linear-gradient(to left, ${MYOB_BRAND_COLOR}, ${MYOB_BRAND_COLOR_DARK})`
      }}>
      <RegularContainer>
        <h2>MYOB</h2>
        <h3>Online accounting software that empowers your small business.</h3>
        <div className={styles.featuresPanel}>
          <div>
            <h4>Manage your business on any device</h4>
            <p>MYOB is 100% cloud based so you can work anywhere and anytime you want.</p>

            <h4>Packed with dynamic and easy-to-use features for PCs</h4>
            <p>Smart features that save you time and automate your daily business admin.</p>
            <h4>Take control of your stock</h4>
            <p>
              Spot best sellers and poor performers ahead of time with advanced and intuitive inventory management
              features.
            </p>
            <ul>
              <li>Easy online invoicing and quotes</li>
              <li>Smart inventory management</li>
              <li>Detailed multi currency features</li>
              <li>Interactive bills and expenses</li>
              <li>Actionable business insights</li>
              <li>Clear and compliant payroll</li>
            </ul>
            <div className={styles.ctaGroup}>
              <ButtonAnchor kind="reverse" target="_blank" href="http://www.myob.com.au/trial/au/accountright-standard">
                Try MYOB for Free
              </ButtonAnchor>
            </div>
          </div>
          <div>
            <img src="/marketing/integrations/myob/ar-inventory.png" alt="MYOB AccountRight Inventory" />
          </div>
        </div>
      </RegularContainer>
    </div>

    <div className={styles.pageSection}>
      <RegularContainer>
        <h2>Connecting Fieldfolio to MYOB is easy</h2>

        <h4>Getting started tutorial</h4>
        <ul className={styles.helpLinkList}>
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${process.env.NEXT_PUBLIC_HELP_URL}/en/articles/6201239-setup-the-myob-accountright-live-integration`}>
              Setup the MYOB AccountRight Live Integration
            </a>
          </li>
        </ul>

        <h4>Documentation</h4>
        <ul className={styles.helpLinkList}>
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${process.env.NEXT_PUBLIC_HELP_URL}/en/articles/6201252-myob-accountright-live-integration`}>
              MYOB AccountRight Live integration
            </a>
          </li>
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${process.env.NEXT_PUBLIC_HELP_URL}/en/articles/6201251-myob-accountright-live-integration-settings`}>
              MYOB AccountRight Live integration settings
            </a>
          </li>
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${process.env.NEXT_PUBLIC_HELP_URL}/en/articles/6201257-myob-accountright-live-integration-log`}>
              MYOB AccountRight Live Integration Log
            </a>
          </li>
        </ul>
      </RegularContainer>
    </div>

    <div className={styles.pageSection}>
      <RegularContainer>
        <h3>Read our help docs</h3>
        <ButtonAnchor
          kind="primary"
          icon="external-link"
          iconPosition="right"
          target="_blank"
          href={`${process.env.NEXT_PUBLIC_HELP_URL}/en/collections/3413401-wholesalers`}>
          Wholesaler Help
        </ButtonAnchor>
      </RegularContainer>
    </div>

    <footer className={styles.footer}>
      <RegularContainer>
        <h2>Signup and get started today</h2>
        <div className={styles.ctaGroup}>
          <Link href="/signup/wholesaler" passHref>
            <ButtonAnchor kind="primary">Sell on Fieldfolio</ButtonAnchor>
          </Link>
          <ButtonAnchor
            className={styles.brandCtaButton}
            kind="reverse"
            target="_blank"
            href="http://www.myob.com.au/trial/au/accountright-standard">
            Try MYOB for Free
          </ButtonAnchor>
        </div>
      </RegularContainer>
    </footer>
  </BasePage>
)

export default MyobPage
